/**
 * Monitoramento de erro do painel (Sentry).
 *
 * Projeto `agendo-certo-painel`, org `agendocerto`. Sem
 * `VITE_SENTRY_DSN` a inicialização não acontece e nada quebra — é o
 * caso do desenvolvimento local e do CI.
 *
 * POR QUE O PAINEL IMPORTA MAIS QUE O SITE: aqui é onde o dono opera o
 * negócio. Um erro no site é uma visita perdida; um erro aqui é uma
 * agenda que não abre, um agendamento que não salva — e o dono não abre
 * chamado, ele só assume que "o sistema está ruim". Até 03/09/2026 este
 * projeto não tinha monitoramento nenhum: toda falha de front do painel
 * era invisível.
 *
 * Exige `https://*.sentry.io` no `connect-src` da CSP
 * (functions/_middleware.js). Sem isso o SDK carrega, captura tudo
 * direitinho e o envio é bloqueado pelo navegador — sem nada aparecer
 * quebrado na tela.
 */

import * as Sentry from "@sentry/react";

const ENV = import.meta.env.VITE_ENVIRONMENT ?? import.meta.env.MODE ?? "development";
const EM_PRODUCAO = ENV === "production";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: ENV,
    release: import.meta.env.VITE_APP_VERSION,

    // FALSO por causa da LGPD, e é o mesmo compromisso do backend
    // (DataCollection.UserInfo desligado em infrastructure/sentryobs):
    // `true` mandaria IP, cookies e cabeçalhos. Nada disso é necessário
    // para depurar erro de JavaScript.
    sendDefaultPii: false,
    attachStacktrace: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Mascaramento EXPLÍCITO. O painel mostra nome, telefone e
      // histórico de CLIENTE DO DONO — dado de terceiro, não do usuário
      // logado. Um replay sem máscara viraria uma cópia da agenda dele
      // dentro do Sentry. O padrão do SDK hoje já mascara; explícito
      // porque padrão muda entre versões e essa regressão seria
      // invisível.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    tracesSampleRate: EM_PRODUCAO ? 0.2 : 0,
    // O painel fala com a nossa API; propagar o trace para lá liga o
    // erro de tela ao 5xx do servidor na MESMA linha do tempo. Exige
    // Sentry-Trace e Baggage liberados no CORS da API — já estão.
    tracePropagationTargets: [/^\//, /^https:\/\/api\.agendocerto\.com\.br/],

    replaysSessionSampleRate: EM_PRODUCAO ? 0.05 : 0,
    replaysOnErrorSampleRate: EM_PRODUCAO ? 1.0 : 0,

    beforeSend(event) {
      // Em desenvolvimento o SDK fica inicializado (para o código ser o
      // mesmo dos dois lados) mas não envia: um erro de hot-reload não é
      // notícia, e poluiria o painel com ruído que ninguém vai investigar.
      if (!EM_PRODUCAO) return null;
      return event;
    },
  });
}

/**
 * Marca o dono autenticado no escopo. Recebe o UUID interno — NUNCA
 * e-mail, telefone ou nome, o que é o mesmo compromisso do
 * `sendDefaultPii: false` acima e do backend.
 */
export function identificarUsuario(userID: string, establishmentID?: string) {
  Sentry.setUser({ id: userID });
  if (establishmentID) Sentry.setTag("establishment_id", establishmentID);
}

/** Limpa o escopo no logout — sem isto o próximo erro sairia atribuído
 *  a quem acabou de sair. */
export function limparUsuario() {
  Sentry.setUser(null);
}
